import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Avatar,
} from '@mui/material';
import { Edit, ArrowBack, Public, Lock, Visibility, VisibilityOff, Folder as FolderIcon, OpenInNew } from '@mui/icons-material';
import api from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { usePermissions } from '../hooks/usePermissions';
import ProjectPermissionsPanel from '../components/permissions/ProjectPermissionsPanel';
import ProjectRequirementsSection from '../components/requirements/ProjectRequirementsSection';

const statusColors = {
  planning: 'warning',
  in_progress: 'primary',
  completed: 'success',
  on_hold: 'default',
  cancelled: 'error',
};

const statusLabels = {
  planning: 'Planning',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeProject, selectProject, fetchProjects } = useProject();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const permissions = usePermissions(project);

  // Hold latest context values in refs so the fetch effect only re-runs when `id` changes
  const activeProjectRef = useRef(activeProject);
  const selectProjectRef = useRef(selectProject);
  const fetchProjectsRef = useRef(fetchProjects);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    activeProjectRef.current = activeProject;
    selectProjectRef.current = selectProject;
    fetchProjectsRef.current = fetchProjects;
    navigateRef.current = navigate;
  });

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/projects/${id}`, { signal: abortController.signal });
        if (!isMounted) return;
        if (response.data) {
          setProject(response.data);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        if (!isMounted) return;
        if (err.response?.status === 404) {
          // Project is gone (deleted, moved, or no longer accessible).
          // Clear stale selection so the top bar doesn't keep showing it,
          // refresh the list, then send the user to the projects list.
          if (activeProjectRef.current?.id === id) {
            selectProjectRef.current(null);
          }
          fetchProjectsRef.current();
          navigateRef.current('/projects', { replace: true });
          return;
        }
        setError(err.response?.data?.detail || 'Failed to load project.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProject();
    return () => { isMounted = false; abortController.abort(); };
  }, [id]);

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;
    try {
      setDeleting(true);
      await api.delete(`/projects/${id}`);
      navigate('/projects');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error || 'Project not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Container>
    );
  }

  const statusLabel = project.status
    ? (statusLabels[project.status] || project.status.replace(/_/g, ' '))
    : null;
  const statusColor = project.status ? (statusColors[project.status] || 'default') : 'default';

  return (
    <Box sx={{ width: '100%', py: { xs: 2, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
      <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {project.avatar_url ? (
              <Avatar src={project.avatar_url} sx={{ width: 48, height: 48 }} />
            ) : (
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'secondary.main' }}>
                <FolderIcon />
              </Avatar>
            )}
            {project.name}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Project Details</Typography>
                <Stack direction="row" spacing={1}>
                  {project.is_public && (
                    <Button
                      startIcon={<Public />}
                      endIcon={<OpenInNew />}
                      href={`/public/roadmap/${id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      color="success"
                      size="small"
                    >
                      View Public Roadmap
                    </Button>
                  )}
                  {permissions.canEditProject && (
                    <Button
                      startIcon={<Edit />}
                      onClick={() => navigate(`/projects/edit/${id}`)}
                      variant="outlined"
                    >
                      Edit Project
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.description || 'No description provided.'}
                  </Typography>
                </Box>
                <Divider />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Visibility
                      </Typography>
                      <Chip
                        icon={project.is_public ? <Public /> : <Lock />}
                        label={project.is_public ? 'Public' : 'Private'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Default Work Item Visibility
                      </Typography>
                      <Chip
                        icon={project.task_default_public ? <Visibility /> : <VisibilityOff />}
                        label={project.task_default_public ? 'Public' : 'Private'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Project Status
                      </Typography>
                      {statusLabel ? (
                        <Chip label={statusLabel} color={statusColor} size="small" sx={{ textTransform: 'capitalize' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Not set</Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {new Date(project.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          <Box sx={{ mt: 3 }}>
            <ProjectRequirementsSection
              projectId={project.id}
              canEdit={permissions.canEditProject}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <ProjectPermissionsPanel project={project} />

            {permissions.canDeleteProject && (
              <Card elevation={3} sx={{ borderTop: 3, borderColor: 'error.main' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="error">
                    Danger Zone
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Deleting a project will remove all related data. This action cannot be undone.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={handleDeleteProject}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete Project'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectDetailPage;
